import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Divider, CircularProgress, Alert, Tooltip, Box, Stack } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import { fetchScormFileDetails, deleteContentExtraction, deleteScormFileApi } from '../../apis/scormUpload';
import { restartContentExtraction } from '../../apis/restartContentExtraction';
import { deleteScormFile as deleteScormFileAction } from '../../store/slices/scormFilesSlice';
import StatusBadge from '../StatusBadge';
import StartExtractionButton from '../Scorm/ScormUploads/StartExtractionButton';
import ConfirmationDialog from './ConfirmationDialog';
import { formatDate, formatTime, handleFileDownload } from '../../lib/utils';
import DeleteFileButton from '../Scorm/ScormUploads/DeleteFileButton';

const HistoryDialog = ({ open, onClose, scormId }) => {
    const [fileDetails, setFileDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [processes, setProcesses] = useState([]);
    const dispatch = useDispatch();
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        type: 'warning',
        title: '',
        message: '',
        onConfirm: null,
        loading: false,
    });
    const [sortModel, setSortModel] = useState([
        {
            field: 'start_time', // The field to sort by
            sort: 'desc', // The sorting order
        },
    ]);

    const closeConfirmDialog = () => {
        setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
    };

    // ✅ Fetch details on open
    useEffect(() => {
        if (open && scormId) getScormFileDetails();
    }, [open, scormId]);

    const getScormFileDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetchScormFileDetails(scormId);
            setFileDetails(response?.data);
            setProcesses(response?.data?.content_extractions || []);
        } catch (err) {
            setError('Failed to load file details');
            toast.error('Failed to load file details');
        } finally {
            setLoading(false);
        }
    };

    // ✅ Start extraction updates parent
    const handleStartExtraction = (updatedFile) => {
        setFileDetails(updatedFile);
        if (updatedFile.content_extractions) setProcesses([...updatedFile.content_extractions]);
    };

    // ✅ Restart extraction
    const handleRestart = (processId) => {
        setConfirmDialog({
            open: true,
            type: 'warning',
            title: 'Restart Extraction Process',
            message: 'Are you sure you want to restart this extraction process?',
            onConfirm: () => confirmRestart(processId),
            loading: false,
        });
    };

    const confirmRestart = async (processId) => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
            const response = await restartContentExtraction(processId);
            const updatedExtraction = response.scorm_file.content_extractions.find((ex) => ex.id === processId);
            if (updatedExtraction) {
                setProcesses((prev) =>
                    prev.map((proc) => (proc.id === processId ? { ...proc, ...updatedExtraction } : proc))
                );
            }
            toast.success('Restart requested successfully.');
        } catch (err) {
            toast.error('Failed to restart extraction: ' + err);
        } finally {
            closeConfirmDialog();
        }
    };

    // ✅ Delete extraction
    const handleProcessDelete = (processId) => {
        setConfirmDialog({
            open: true,
            type: 'danger',
            title: 'Delete Extraction Process',
            message: 'Are you sure you want to delete this extraction process permanently?',
            confirmText: 'Delete',
            onConfirm: () => confirmProcessDelete(processId),
            loading: false,
        });
    };

    const confirmProcessDelete = async (processId) => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
            await deleteContentExtraction(processId);
            setProcesses((prev) => prev.filter((proc) => proc.id !== processId));
            toast.success('Extraction deleted successfully.');
        } catch (err) {
            toast.error('Failed to delete extraction: ' + err);
        } finally {
            closeConfirmDialog();
        }
    };

    // ✅ Delete file
    const handleDelete = (fileId) => {
        setConfirmDialog({
            open: true,
            type: 'danger',
            title: 'Delete File',
            message: 'Are you sure you want to delete this file permanently? ',
            confirmText: 'Delete File',
            onConfirm: () => confirmFileDelete(fileId),
            loading: false,
        });
    };

    const confirmFileDelete = async (fileId) => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
            await deleteScormFileApi(fileId);
            toast.success('File deleted successfully');
            dispatch(deleteScormFileAction(fileId));
            onClose();
        } catch (err) {
            toast.error('Failed to delete file: ' + err);
        } finally {
            closeConfirmDialog();
        }
    };

    // ✅ DataGrid columns
    const columns = [
        { field: 'file_name', headerName: 'File Name', flex: 2 },
        { field: 'started_at', headerName: 'Date', flex: 1, valueFormatter: (value) => formatDate(value) },
        {
            field: 'start_time',
            headerName: 'Start Time',
            flex: 0.8,
            valueGetter: (_, row) => row?.started_at,
            valueFormatter: (value) => formatTime(value),
        },
        {
            field: 'finish_time',
            headerName: 'Finish Time',
            flex: 0.8,
            valueGetter: (_, row) => row?.finished_at,
            valueFormatter: (value) => formatTime(value),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1.2,
            renderCell: (params) => <StatusBadge status={params?.value || '-'} type="extraction" />,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.8,
            sortable: false,
            renderCell: (params) => {
                const proc = params?.row;
                if (!proc) return null;
                const canDownload = proc?.download_link != null;
                const canShow = proc?.status === 'completed' || proc?.status === 'failed';
                return (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ height: '100%' }}>
                        <Tooltip title={canShow ? 'Restart Extraction' : ''}>
                            <span
                                className="material-symbols-rounded"
                                style={{
                                    color: canShow ? '#4C43A3' : '#4C43A366',
                                    cursor: canShow ? 'pointer' : 'not-allowed',
                                    fontSize: '24px',
                                }}
                                onClick={() => canShow && handleRestart(proc.id)}
                            >
                                replay
                            </span>
                        </Tooltip>
                        <Tooltip title={canDownload ? 'Download Extraction' : ''}>
                            <span
                                onClick={() => canDownload && handleFileDownload(proc?.download_link)}
                                className="material-symbols-rounded"
                                style={{
                                    color: canDownload ? '#4C43A3' : '#4C43A366',
                                    cursor: canDownload ? 'pointer' : 'not-allowed',
                                    fontSize: '24px',
                                }}
                            >
                                download
                            </span>
                        </Tooltip>
                        <Tooltip title={canShow ? 'Delete Extraction' : ''}>
                            <span
                                className="material-symbols-rounded"
                                style={{
                                    color: canShow ? '#F64E60' : '#F64E6066',
                                    cursor: canShow ? 'pointer' : 'not-allowed',
                                    fontSize: '24px',
                                }}
                                onClick={() => canShow && handleProcessDelete(proc.id)}
                            >
                                delete
                            </span>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ];

    const rows =
        processes?.map((extraction, index) => ({
            id: extraction.id || `extraction-${index}`,
            file_name: extraction.file_name || fileDetails?.file_name || '-',
            started_at: extraction.started_at,
            finished_at: extraction.finished_at,
            status: extraction.status || '-',
            download_link: extraction.download_link,
        })) || [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{ '& .MuiDialog-paper': { borderRadius: 3, padding: '.75rem 1.5rem' } }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem 0',
                    borderBottom: '.0625rem solid #E1E8ED',
                    fontFamily: 'Inter',
                }}
            >
                {fileDetails?.file_name}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        minWidth: 20,
                        border: '.125rem solid #444',
                        cursor: 'pointer',
                        borderRadius: '.375rem',
                    }}
                    onClick={onClose}
                >
                    <CloseIcon sx={{ fontSize: '.875rem', color: '#444' }} />
                </div>
            </DialogTitle>

            <Divider />

            {/* Body */}
            <DialogContent sx={{ px: 0, py: 3, overflow: 'auto' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : fileDetails ? (
                    <>
                        {/* File Details */}
                        <div className="file_details_container">
                            <div>
                                <span>Date</span>
                                <span>
                                    {fileDetails.created_at
                                        ? new Date(fileDetails.created_at).toLocaleDateString(undefined, {
                                              day: 'numeric',
                                              month: 'long',
                                              year: 'numeric',
                                          })
                                        : '-'}
                                </span>
                            </div>
                            <div>
                                <span>Size (MB)</span>
                                <span>{fileDetails.file_size || '-'}</span>
                            </div>
                            <div>
                                <span>Status</span>
                                <span>
                                    <StatusBadge
                                        status={
                                            fileDetails?.last_extraction == null
                                                ? fileDetails?.status || '-'
                                                : fileDetails?.last_extraction?.status || '-'
                                        }
                                        type={fileDetails?.last_extraction == null ? 'file' : 'extraction'}
                                        sx={{
                                            minWidth: 'min-content !important',
                                            display: 'flex !important',
                                            flexDirection: 'row !important',
                                        }}
                                    />
                                </span>
                            </div>
                            <div>
                                <span>Actions</span>
                                <span style={{ display: 'inline-flex' }}>
                                    <StartExtractionButton
                                        file={fileDetails}
                                        onStarted={
                                            fileDetails?.last_extraction?.download_link != null && handleStartExtraction
                                        }
                                    />
                                    <DeleteFileButton file={fileDetails} onDelete={handleDelete} />
                                </span>
                            </div>
                        </div>

                        {/* Extractions Table */}
                        <div className="custom_table_container">
                            <div className="table_filters">
                                <h6>Extracted Content</h6>
                            </div>
                            <div className="custom_table_responsive">
                                <DataGrid
                                    rows={rows}
                                    columns={columns}
                                    disableColumnMenu
                                    disableRowSelectionOnClick
                                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                                    pageSizeOptions={[5, 10, 25]}
                                    rowHeight={52}
                                    localeText={{ noRowsLabel: 'No Extracted Content Found' }}
                                    sortModel={sortModel}
                                    onSortModelChange={(newModel) => setSortModel(newModel)}
                                />
                            </div>
                        </div>

                        <ConfirmationDialog {...confirmDialog} onClose={closeConfirmDialog} />
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default HistoryDialog;
